import React, { useState, useRef, useMemo } from 'react';
import { cx } from '@emotion/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import SwiperCore from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { Box } from '@mui/material';
import { Page } from '@/graphql/types';
import config from '@/config/config';
import styles from './FlyerCardSwiper.styles';

interface FlyerCardSwiperProps {
  id: number;
  districtId?: string | null;
  pages: Page[];
  thumbnail: string;
  flyerTitle: string;
  isListMode: boolean;
  onLoadFlyer: () => void;
  className?: string;
  swiperRef?: React.MutableRefObject<SwiperCore | null>;
  onSlideChange?: (core: SwiperCore) => void;
  isExpired: boolean;
}

export const FlyerCardSwiper: React.FC<FlyerCardSwiperProps> = ({
  id,
  districtId,
  pages,
  thumbnail,
  flyerTitle,
  isListMode,
  onLoadFlyer,
  className,
  swiperRef: externalSwiperRef,
  onSlideChange: externalOnSlideChange,
  isExpired,
}) => {
  const [activeSlide, setActiveSlide] = useState(1);
  const pageCount = pages.length || 1;
  const hasMultiplePages = pageCount > 1;
  const internalSwiperRef = useRef<SwiperCore | null>(null);
  const swiperRef = externalSwiperRef || internalSwiperRef;

  const slidesPerView = 1;
  const spaceBetween = 0;

  const handleSlideChange = (core: SwiperCore) => {
    const newActiveIndex = core.activeIndex + 1;
    setActiveSlide(newActiveIndex);
    if (externalOnSlideChange) {
      externalOnSlideChange(core);
    }
  };

  const handlePaginationClick = (index: number) => {
    swiperRef.current?.slideTo(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowLeft') {
      const prevIndex = Math.max(0, activeSlide - 2);
      swiperRef.current?.slideTo(prevIndex);
    } else if (event.key === 'ArrowRight') {
      const nextIndex = Math.min(pageCount - 1, activeSlide);
      swiperRef.current?.slideTo(nextIndex);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePaginationClick(index);
    }
  };

  const renderCustomPagination = () => (
    <ul className={cx(styles.customPagination)}>
      {pages.map((_, index) => (
        <li key={index}>
          <button
            type="button"
            role="button"
            tabIndex={0}
            className={cx(styles.paginationButton, {
              [styles.paginationButtonActive]: index + 1 === activeSlide,
            })}
            aria-label={`Go to slide ${index + 1} of ${pageCount}`}
            aria-current={index + 1 === activeSlide ? 'true' : undefined}
            aria-pressed={index + 1 === activeSlide ? 'true' : 'false'}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              handlePaginationClick(index);
            }}
            onKeyDown={event => {
              event.preventDefault();
              event.stopPropagation();
              handleKeyDown(event, index);
            }}
          >
            <span className="sr-only">{`Slide ${index + 1}`}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  if (!hasMultiplePages || !isListMode || isExpired) {
    return (
      <Box
        sx={{
          aspectRatio: isListMode ? 'auto' : '1 / 1.29',
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          marginBottom: '16px',
        }}
      >
        <img
          src={
            isListMode
              ? `https://flyers-bff.${config.PEACHJAR_DOMAIN}/view/district/${districtId}/flyer/${id}/1.jpg`
              : thumbnail
          }
          alt={flyerTitle}
          onLoad={onLoadFlyer}
          data-testid="flyerCardThumbnail"
          className={cx(styles.flyerImage)}
        />
      </Box>
    );
  }

  const handleContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      const prevIndex = Math.max(0, activeSlide - 2);
      swiperRef.current?.slideTo(prevIndex);
    } else if (event.key === 'ArrowRight') {
      const nextIndex = Math.min(pageCount - 1, activeSlide);
      swiperRef.current?.slideTo(nextIndex);
    }
  };

  return (
    <div
      className={cx(styles.swiperContainer, className)}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      role="region"
      aria-label={`Flyer ${flyerTitle} with ${pageCount} pages`}
    >
      <Swiper
        onSwiper={swiper => {
          swiperRef.current = swiper;
        }}
        simulateTouch={true}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        centeredSlides={false}
        modules={[Pagination]}
        className={styles.swiper}
        onSlideChange={handleSlideChange}
        data-testid="flyerCardSwiper"
      >
        {pages.map((page, index) => {
          const ariaDescribedby = `flyer-card-${id}-page-${page.pageNumber}`;
          const slideLabel = `flyer-card-${id}-slide-${index + 1}-label`;
          const slideKey = `flyer-card-${id}-slide-${page.pageNumber}`;
          return (
            <li key={slideKey} role="listitem">
              <SwiperSlide
                id={`flyer-card-${id}-slide-${index + 1}`}
                aria-roledescription="slide"
                aria-labelledby={slideLabel}
                data-testid="flyerCardSlideItem"
                key={slideKey}
              >
                <>
                  <span id={slideLabel} className="sr-only">
                    Slide {index + 1} of {pages.length}
                  </span>
                  <div className="swiper-zoom-container">
                    <img
                      src={
                        isListMode
                          ? `https://flyers-bff.${config.PEACHJAR_DOMAIN}/view/district/${districtId}/flyer/${id}/${page.pageNumber}.jpg`
                          : page.imageUrl
                      }
                      className={cx(styles.flyerImage)}
                      alt={`Image of a flyer titled ${flyerTitle} - Page ${page.pageNumber}`}
                      aria-describedby={ariaDescribedby}
                      onLoad={onLoadFlyer}
                      data-testid={`flyerCardPageThumbnail-${index}`}
                    />
                  </div>
                </>
              </SwiperSlide>
            </li>
          );
        })}
      </Swiper>

      <div className={cx(styles.paginationContainer)}>{renderCustomPagination()}</div>
    </div>
  );
};
